import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

/* ------------------------------------------------------------------
   Drop this file next to Home.jsx:  import { ... } from './HomeUpgrades';
   Put <style>{upgradeCss}</style> once inside Home's root div.
------------------------------------------------------------------- */

const prefersReduced = () =>
  typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

/* 1. Headline: words rise in one by one with a blur-to-sharp effect */
export function SplitHeadline({ lines, className = '' }) {
  let n = 0;
  return (
    <h1 className={className}>
      {lines.map((line, li) => (
        <span key={li} className="block whitespace-nowrap">
          {line.split(' ').map((w) => (
            <span
              key={`${li}-${w}-${n}`}
              className="word-in grad-text mr-[0.25em] inline-block"
              style={{ animationDelay: `${n++ * 110}ms` }}
            >
              {w}
            </span>
          ))}
        </span>
      ))}
    </h1>
  );
}

/* 2. Aurora background: drifting colour blobs + faint grid. Put inside a relative hero. */
export function Aurora() {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
      <div className="aurora-blob a1" />
      <div className="aurora-blob a2" />
      <div className="aurora-blob a3" />
      <div className="absolute inset-0 grid-fade" />
    </div>
  );
}

/* 3. Count-up number that starts when scrolled into view */
export function CountUp({ to, suffix = '', duration = 1600 }) {
  const ref = useRef(null);
  const [v, setV] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let raf;
    const run = () => {
      const s = performance.now();
      const tick = (t) => {
        const p = Math.min(1, (t - s) / duration);
        setV(Math.round(to * (1 - Math.pow(1 - p, 3))));
        if (p < 1) raf = requestAnimationFrame(tick);
      };
      raf = requestAnimationFrame(tick);
    };
    const io = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { prefersReduced() ? setV(to) : run(); io.disconnect(); }
    }, { threshold: 0.4 });
    io.observe(el);
    return () => { io.disconnect(); cancelAnimationFrame(raf); };
  }, [to, duration]);
  return <span ref={ref}>{v.toLocaleString('en-IN')}{suffix}</span>;
}

/* 4. Trust strip. Pass REAL numbers from your API (e.g. /stats) instead of the defaults. */
export function StatsStrip({ stats = [
  { n: 1000, s: '+', label: 'Live jobs' },
  { n: 500, s: '+', label: 'Hiring companies' },
  { n: 10000, s: '+', label: 'Job seekers' },
  { n: 12, s: '', label: 'Industries' },
] }) {
  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-8 mt-14">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((s) => (
          <div key={s.label} className="spot rounded-2xl bg-white/80 backdrop-blur border border-purple-100 px-5 py-6 text-center shadow-sm">
            <p className="text-3xl sm:text-4xl font-extrabold grad-text"><CountUp to={s.n} suffix={s.s} /></p>
            <p className="text-sm text-gray-500 mt-1 font-medium">{s.label}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

/* 5. Mouse-follow glow card. Wrap any card: <Spotlight className="..."> */
export function Spotlight({ children, className = '' }) {
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    e.currentTarget.style.setProperty('--mx', `${e.clientX - r.left}px`);
    e.currentTarget.style.setProperty('--my', `${e.clientY - r.top}px`);
  };
  return <div onMouseMove={onMove} className={`spot ${className}`}>{children}</div>;
}

/* 6. Magnetic button: gently follows the cursor */
export function Magnetic({ children, className = '', ...rest }) {
  const ref = useRef(null);
  const move = (e) => {
    if (prefersReduced() || !ref.current) return;
    const r = ref.current.getBoundingClientRect();
    const x = (e.clientX - (r.left + r.width / 2)) * 0.25;
    const y = (e.clientY - (r.top + r.height / 2)) * 0.35;
    ref.current.style.transform = `translate(${x}px, ${y}px)`;
  };
  const leave = () => { if (ref.current) ref.current.style.transform = ''; };
  return (
    <span ref={ref} onMouseMove={move} onMouseLeave={leave} className={`magnetic inline-block ${className}`} {...rest}>
      {children}
    </span>
  );
}

/* 7. VIDEO SECTION ---------------------------------------------------
   Use ONE of:  youtubeId="abc123"   or   src="/videos/planb-intro.mp4"
   The video only loads after the visitor clicks play (keeps the page fast). */
export function VideoShowcase({ youtubeId, src, poster, title = 'See how PlanB Jobs works' }) {
  const [playing, setPlaying] = useState(false);
  const thumb = poster || (youtubeId ? `https://img.youtube.com/vi/${youtubeId}/maxresdefault.jpg` : null);

  return (
    <section id="watch" className="scroll-mt-24 max-w-7xl mx-auto px-6 lg:px-8 py-20 lg:py-24">
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.35fr] gap-10 lg:gap-14 items-center">
        <div>
          <p className="inline-block text-xs font-semibold text-[#7C3AED] bg-[#F5F0FA] rounded-full px-3 py-1.5 mb-5">
            Watch · 60 seconds
          </p>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-4">{title}</h2>
          <p className="text-gray-500 leading-relaxed mb-6 max-w-md">
            From creating your profile to getting matched with employers. See how a job search on PlanB Jobs looks, start to finish.
          </p>
          <ol className="space-y-3 text-sm text-gray-600">
            {['Create your free profile', 'Get jobs matched to your skills', 'Apply in one click', 'Track every application'].map((t, i) => (
              <li key={t} className="flex items-center gap-3">
                <span className="w-7 h-7 rounded-full brand-gradient text-white text-xs font-bold flex items-center justify-center shrink-0">{i + 1}</span>
                {t}
              </li>
            ))}
          </ol>
          <Link to="/register" className="shine-btn brand-gradient text-white inline-block mt-8 px-8 py-3.5 rounded-xl font-semibold text-sm shadow-[0_10px_30px_-8px_rgba(124,58,237,0.6)]">
            Create free profile
          </Link>
        </div>

        {/* animated gradient frame */}
        <div className="relative rounded-[1.75rem] p-[3px] overflow-hidden shadow-[0_40px_90px_-30px_rgba(124,58,237,0.6)]">
          <div className="spin-border" aria-hidden="true" />
          <div className="relative rounded-[1.6rem] overflow-hidden bg-[#12081F] aspect-video">
            {playing ? (
              youtubeId ? (
                <iframe
                  className="absolute inset-0 w-full h-full"
                  src={`https://www.youtube-nocookie.com/embed/${youtubeId}?autoplay=1&rel=0&modestbranding=1`}
                  title={title}
                  allow="autoplay; encrypted-media; picture-in-picture"
                  allowFullScreen
                />
              ) : (
                <video className="absolute inset-0 w-full h-full" src={src} poster={thumb || undefined} controls autoPlay playsInline />
              )
            ) : (
              <button
                type="button"
                onClick={() => setPlaying(true)}
                aria-label="Play video"
                className="group absolute inset-0 w-full h-full flex items-center justify-center focus:outline-none focus-visible:ring-4 focus-visible:ring-[#DB2777]/60"
                style={thumb ? { backgroundImage: `url(${thumb})`, backgroundSize: 'cover', backgroundPosition: 'center' } : { backgroundImage: 'linear-gradient(135deg,#4C1D95,#BE185D)' }}
              >
                <span className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
                <span className="relative flex items-center justify-center">
                  <span className="play-ring" /><span className="play-ring" style={{ animationDelay: '.9s' }} />
                  <span className="relative w-20 h-20 rounded-full bg-white text-[#7C3AED] flex items-center justify-center shadow-2xl transition-transform duration-300 group-hover:scale-110">
                    <svg viewBox="0 0 24 24" className="w-8 h-8 ml-1" fill="currentColor"><path d="M8 5v14l11-7z" /></svg>
                  </span>
                </span>
                <span className="absolute bottom-4 left-5 text-white text-sm font-semibold">▶ Play intro</span>
              </button>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

/* 8. How it works: the connecting line fills as you scroll */
export function HowItWorks() {
  const ref = useRef(null);
  const [p, setP] = useState(0);
  useEffect(() => {
    const on = () => {
      const el = ref.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const v = (window.innerHeight * 0.75 - r.top) / r.height;
      setP(Math.max(0, Math.min(1, v)));
    };
    on();
    window.addEventListener('scroll', on, { passive: true });
    return () => window.removeEventListener('scroll', on);
  }, []);
  const steps = [
    { t: 'Create your profile', d: 'Add skills, experience and your preferred city in minutes.' },
    { t: 'Get matched', d: 'See jobs ranked by how well they fit your skills and location.' },
    { t: 'Apply and practise', d: 'Apply in one click and rehearse with the free mock interview.' },
    { t: 'Get hired', d: 'Track applications, interviews and offers in one place.' },
  ];
  return (
    <section ref={ref} className="max-w-5xl mx-auto px-6 lg:px-8 py-20">
      <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-12">From sign-up to offer letter.</h2>
      <div className="relative pl-12">
        <div className="absolute left-[19px] top-2 bottom-2 w-[3px] rounded bg-purple-100" />
        <div className="absolute left-[19px] top-2 w-[3px] rounded brand-gradient" style={{ height: `calc(${p * 100}% - 8px)` }} />
        {steps.map((s, i) => {
          const on = p > (i + 0.3) / steps.length;
          return (
            <div key={s.t} className="relative pb-10 last:pb-0">
              <span className={`absolute -left-12 top-0 w-10 h-10 rounded-full flex items-center justify-center text-sm font-extrabold transition-all duration-500 ${on ? 'brand-gradient text-white scale-110 shadow-lg shadow-[#7C3AED]/40' : 'bg-white border-2 border-purple-100 text-[#7C3AED]'}`}>
                {i + 1}
              </span>
              <h3 className={`text-lg font-bold transition-colors ${on ? 'text-[#1E0F3D]' : 'text-gray-400'}`}>{s.t}</h3>
              <p className="text-sm text-gray-500 mt-1 max-w-md">{s.d}</p>
            </div>
          );
        })}
      </div>
    </section>
  );
}

/* 9. Sticky bottom CTA for logged-out visitors (keeps them converting) */
export function StickyCta({ isLoggedIn }) {
  const [show, setShow] = useState(false);
  const [closed, setClosed] = useState(false);
  useEffect(() => {
    const on = () => setShow(window.scrollY > 700);
    on();
    window.addEventListener('scroll', on, { passive: true });
    return () => window.removeEventListener('scroll', on);
  }, []);
  if (isLoggedIn || closed) return null;
  return (
    <div className={`fixed bottom-5 left-1/2 -translate-x-1/2 z-[85] w-[calc(100%-2rem)] max-w-xl transition-all duration-500 ${show ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10 pointer-events-none'}`}>
      <div className="flex items-center gap-3 rounded-2xl bg-[#1E0F3D]/95 backdrop-blur text-white pl-5 pr-3 py-3 shadow-2xl border border-white/10">
        <p className="flex-1 text-sm font-semibold">Get jobs matched to you. It's free.</p>
        <Link to="/register" className="cta-glow brand-gradient px-5 py-2 rounded-xl text-sm font-bold whitespace-nowrap">Join free</Link>
        <button type="button" onClick={() => setClosed(true)} aria-label="Dismiss" className="w-8 h-8 rounded-full hover:bg-white/10 text-white/70">✕</button>
      </div>
    </div>
  );
}

/* ---------------------------- CSS ---------------------------- */
export const upgradeCss = `
.grad-text{background-image:linear-gradient(90deg,#7C3AED,#DB2777,#7C3AED);background-size:200% auto;-webkit-background-clip:text;background-clip:text;-webkit-text-fill-color:transparent;color:transparent;animation:shimmer 5s linear infinite}
@keyframes word-in{from{opacity:0;transform:translateY(28px) rotateX(-40deg);filter:blur(10px)}to{opacity:1;transform:none;filter:blur(0)}}
.word-in{animation:word-in .8s cubic-bezier(.2,.8,.2,1) both,shimmer 5s linear infinite}

.aurora-blob{position:absolute;border-radius:9999px;filter:blur(70px);mix-blend-mode:multiply;opacity:.55}
.aurora-blob.a1{width:34rem;height:34rem;background:#A78BFA;top:-12rem;right:-6rem;animation:drift1 16s ease-in-out infinite}
.aurora-blob.a2{width:28rem;height:28rem;background:#F9A8D4;bottom:-10rem;left:-6rem;animation:drift2 19s ease-in-out infinite}
.aurora-blob.a3{width:22rem;height:22rem;background:#C4B5FD;top:30%;left:35%;animation:drift3 22s ease-in-out infinite}
@keyframes drift1{0%,100%{transform:translate(0,0) scale(1)}50%{transform:translate(-60px,50px) scale(1.15)}}
@keyframes drift2{0%,100%{transform:translate(0,0) scale(1)}50%{transform:translate(70px,-40px) scale(1.1)}}
@keyframes drift3{0%,100%{transform:translate(0,0)}33%{transform:translate(50px,-30px)}66%{transform:translate(-40px,40px)}}
.grid-fade{background-image:linear-gradient(rgba(124,58,237,.07) 1px,transparent 1px),linear-gradient(90deg,rgba(124,58,237,.07) 1px,transparent 1px);background-size:44px 44px;-webkit-mask-image:radial-gradient(ellipse at 50% 40%,#000 30%,transparent 75%);mask-image:radial-gradient(ellipse at 50% 40%,#000 30%,transparent 75%)}

.spot{position:relative;overflow:hidden;transition:transform .3s ease,box-shadow .3s ease}
.spot::before{content:'';position:absolute;inset:0;pointer-events:none;opacity:0;transition:opacity .3s;background:radial-gradient(260px circle at var(--mx,50%) var(--my,50%),rgba(124,58,237,.16),transparent 70%)}
.spot:hover{transform:translateY(-4px);box-shadow:0 24px 50px -24px rgba(124,58,237,.5)}
.spot:hover::before{opacity:1}

.magnetic{transition:transform .25s cubic-bezier(.2,.8,.2,1)}
.spin-border{position:absolute;inset:-50%;background:conic-gradient(from 0deg,#7C3AED,#DB2777,#F59E0B,#7C3AED);animation:spin-b 6s linear infinite}
@keyframes spin-b{to{transform:rotate(360deg)}}
.play-ring{position:absolute;width:5rem;height:5rem;border-radius:9999px;border:2px solid rgba(255,255,255,.7);animation:ring-out 1.8s ease-out infinite}
@keyframes ring-out{from{transform:scale(1);opacity:.8}to{transform:scale(2.2);opacity:0}}

@media (prefers-reduced-motion:reduce){
  .grad-text,.word-in,.aurora-blob,.spin-border,.play-ring{animation:none}
  .word-in{opacity:1;filter:none}
  .spot,.magnetic{transition:none}
}
`;